import { ClientRoleModel } from "@app/client/clientRoleModel";
import { SnowflakeWarehouseSizeEnum } from "@app/client/snowflakeWarehouseSizeEnum";
import { ClientInsuranceModel } from "@app/insurance/models";
import { DashboardModel } from "@app/user/dashboardModel";
import RoleModel from "@app/user/roleModel";

interface ClientBaseModel {
  bulkUploadLimitHourWindow: number;
  bulkUploadRecordLimit: number;
  clientId: number;
  crossProjectDatabaseConnectionId: number;
  dashboards: Array<DashboardModel>;
  databaseConnectionId: number;
  dateDeactivated: string;
  disableAccessCacheReads: boolean;
  disableAccessCacheWrites: boolean;
  domains: Array<{ domain: string }>;
  enterpriseComplianceDashboardDatabaseConnectionId: number;
  holdSoftwareRelease?: boolean;
  isActive: boolean;
  isQuestInternal: boolean;
  clientType: ClientType;
  authorizedInsurances: Array<ClientInsuranceModel>;
  logoUrl: string;
  name: string;
  networkServicingProviderReportLimit: number;
  outreachClientXid: string;
  outreachMetricsActivationDate: string;
  pciDashboardDatabaseConnectionId: number;
  prospectMarketServicingProviderReportLimit: number;
  providerAffiliationAuthorizedStates: string;
  roles: Array<ClientRoleModel>;
  sfAccountId: string;
  snowflakeWarehouseDefault: SnowflakeWarehouseSizeEnum;
  snowflakeWarehouseMax: SnowflakeWarehouseSizeEnum;
  snowflakeWarehouseMin: SnowflakeWarehouseSizeEnum;
  snowflakeBackgroundWarehouseMin: SnowflakeWarehouseSizeEnum;
  snowflakeBackgroundWarehouseDefault: SnowflakeWarehouseSizeEnum;
  snowflakeBackgroundWarehouseMax: SnowflakeWarehouseSizeEnum;
  // write-only: submit a new value to rotate it; leave "" on an update to keep the existing value.
  // optional because it's never populated when reading a client back from the API - see Client.storageAccountName
  storageConnectionString?: string;
  storageContainerName: string;

  resourceAdminGuestLoginsAllowed?: boolean;
  questViewerGuestLoginsAllowed?: boolean;
  restrictedUserCreationEnabled?: boolean;
  networkBenchmarkingDataRights: ClientNetworkBenchmarkingDataRightsEnum;
  networkBenchmarkingDataSource: boolean;
  quinnWeeklyPromptLimit: number;
  showInternalClientResource?: boolean;
  regulatorClientIds?: number[];
  skipTerminationTimeDistanceRecalcDefaultValue: boolean;
}

interface Client extends ClientBaseModel {
  defaultUserRoles: Array<ClientRoleModel>;
  questViewerGuestUserRoles: Array<ClientRoleModel>;
  resourceAdminGuestUserRoles: Array<ClientRoleModel>;
  softwareReleases: Array<ClientSoftwareReleaseModel>;
  isTwoSidedRegulatorEligible: boolean;
  isDataShareEligible: boolean;
  // non-secret identifier of the client's storage account, populated by the API on read; the storage
  // connection string itself is never returned (see storageConnectionString below, which is write-only).
  // optional because write-direction payloads (create/update) never set it
  storageAccountName?: string;
}


interface ClientToApi extends ClientBaseModel {
  addedSoftwareReleaseIds: number[];
  removedSoftwareReleaseIds: number[];
}

// Client creation always requires a full connection string, unlike updates (omitting it preserves the
// existing value) or reads (the API never returns it) - see ClientBaseModel.storageConnectionString.
interface ClientCreatePayload extends Omit<Client, "storageConnectionString"> {
  storageConnectionString: string;
}

interface NewClientDefaults {
  defaultStorageConnectionString:string;
  defaultDatabaseConnectionId: number;
  defaultRoles: Array<RoleModel>;
  defaultDashboards: Array<DashboardModel>;
}

interface ClientSoftwareReleaseModel {
  softwareReleaseId: number;
  productSuiteId: number;
  releaseYear: number;
  releaseQuarter: number;
  dateReleased: Date;
  releasedToClient: boolean;
}

interface ClientListItem {
  clientId: number;
  name: string;
}

enum ClientNetworkBenchmarkingDataRightsEnum {
  NotFound = "Not Found",
  FullExplicitRights = "Full Explicit Rights",
  NoExplicitRights = "No Explicit Rights",
  CustomRightsBlindSource = "Custom Rights - Blind Source",
  CustomRightsOther = "Custom Rights - Other",
}

enum ClientType {
  Unknown = "Unknown",
  Issuer = "Issuer",
  Regulator = "Regulator",
  TwoSidedRegulator = "Two-Sided Regulator",
  InternalClientResourceProcessor = "Internal Client Resource Processor",
  QuestInternal = "Quest Internal",
}

export { Client, ClientCreatePayload, ClientListItem, ClientNetworkBenchmarkingDataRightsEnum, ClientSoftwareReleaseModel, ClientToApi, ClientType, NewClientDefaults };
